import {
    type ContactInfo,
    type RealRewardProps,
    RealRewardStep,
} from "@hg/activity-utils"
import cls from "classnames"
import { useCallback, useMemo, useState } from "react"

import { ModalContent } from "@/components/Modal"
import StrokeText from "@/components/StrokeText"
import Toast from "@/components/Toast"
import { i18n } from "@/i18n"
import { RuleModal } from "./Rule"

import styles from "./index.module.scss"

const removeEnter = (str: string) => {
    return str
        .replace(/[\n\r]+/g, "") // 去掉换行
        .replace(/\s+/g, "") // 去掉所有空格（地址通常不需要空格）
        .trim()
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const RealReward: React.FC<RealRewardProps> = (props) => {
    const {
        reward,
        step,
        loading,
        contact,
        onContactChange,
        onStepChange,
        onSubmitContact,
        onClose,
    } = props

    const c = contact as ContactInfo

    const [isSecondConfirm, setIsSecondConfirm] = useState(false)

    const combineAddress = useMemo(() => {
        return [
            contact?.province,
            contact?.city,
            contact?.county,
            removeEnter(contact?.address),
        ]
            .filter(Boolean)
            .join(" ")
    }, [contact])

    const isPreview = step === RealRewardStep.PREVIEW

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onContactChange({
                ...contact,
                [e.target.name]: e.target.value,
            } as ContactInfo)
        },
        [contact, onContactChange],
    )

    const validate = useCallback((): string | null => {
        if (!c.name) return i18n.getRecall("message.name_empty")
        if (!c.phone) return i18n.getRecall("message.phone_empty")
        if (!/^\d{11}$/.test(c.phone))
            return i18n.getRecall("message.phone_invalid")
        if (!(c as any).email) return i18n.getRecall("message.email_empty")
        if (!EMAIL_RE.test((c as any).email))
            return i18n.getRecall("message.email_invalid")
        if (!combineAddress) return i18n.getRecall("message.address_empty")
        return null
    }, [c, combineAddress])

    const showCancelBtn = useMemo(() => {
        return step === RealRewardStep.CONFIRM
    }, [step])

    const handleClickCancel = useCallback(() => {
        if (step === RealRewardStep.CONFIRM) {
            if (isSecondConfirm) {
                setIsSecondConfirm(false)
            } else {
                onStepChange(RealRewardStep.EDIT)
            }
        }
    }, [step, onStepChange, isSecondConfirm])

    const showOkBtn = useMemo(() => {
        return ![RealRewardStep.DONE, RealRewardStep.PREVIEW].includes(step)
    }, [step])

    const handleClickOk = useCallback(() => {
        if (loading) {
            return
        }

        if (step === RealRewardStep.CONFIRM) {
            if (isSecondConfirm) {
                onSubmitContact()
            } else {
                setIsSecondConfirm(true)
            }
        } else if (step === RealRewardStep.EDIT) {
            const err = validate()
            if (err) {
                Toast.message(err)
                return
            }
            onStepChange(RealRewardStep.CONFIRM)
        }
    }, [
        step,
        onSubmitContact,
        onStepChange,
        isSecondConfirm,
        loading,
        validate,
    ])

    const okText = useMemo(() => {
        if (step === RealRewardStep.CONFIRM) {
            return i18n.get("common.ok")
        }
        return i18n.get("common.next")
    }, [step])

    const [ruleOpen, setRuleOpen] = useState(false)

    const addressSmaller = combineAddress.length > 44

    return (
        <ModalContent
            size="large"
            onCancel={onClose}
            cancelText={i18n.get("common.back")}
            cancelButtonProps={{ onClick: handleClickCancel }}
            showCancelBtn={showCancelBtn}
            okText={okText}
            onOk={handleClickOk}
            showOkBtn={showOkBtn}
        >
            <div className={styles.congratulations} />
            <div className={styles.realReward}>
                {[RealRewardStep.EDIT, RealRewardStep.PREVIEW].includes(
                    step,
                ) ? (
                    <>
                        <div className={styles.goods}>
                            <div
                                className={styles.imgWrapper}
                                data-key={reward.key}
                            >
                                <img src={reward.src} alt={reward.title} />
                            </div>
                            <div className={styles.name}>
                                <StrokeText
                                    text={reward.title}
                                    fontFamily="a한글세상B"
                                    fontSize={26}
                                    fillColor="#fffbf1"
                                    borderWidth={4}
                                    borderColor="#614518"
                                    offsetY={0}
                                    padding={3}
                                />
                            </div>
                        </div>
                        <div className={styles.contact}>
                            <input
                                type="text"
                                name="name"
                                value={c.name ?? ""}
                                onChange={handleChange}
                                readOnly={isPreview}
                                placeholder={i18n.get(
                                    "lottery.placeholder_name",
                                )}
                            />
                            <input
                                type="text"
                                name="phone"
                                value={c.phone ?? ""}
                                onChange={handleChange}
                                readOnly={isPreview}
                                inputMode="numeric"
                                placeholder={i18n.get(
                                    "lottery.placeholder_phone",
                                )}
                            />
                            <input
                                type="email"
                                name="email"
                                value={(c as any).email ?? ""}
                                onChange={handleChange}
                                readOnly={isPreview}
                                placeholder={i18n.get("lottery.placeholder_email")}
                            />
                            <textarea
                                name="address"
                                value={c.address ?? ""}
                                onChange={handleChange}
                                readOnly={isPreview}
                                className={cls(
                                    addressSmaller && styles.smaller,
                                )}
                                placeholder={i18n.get(
                                    "lottery.placeholder_address",
                                )}
                            />
                        </div>
                    </>
                ) : null}
                {step === RealRewardStep.CONFIRM && !isSecondConfirm ? (
                    <>
                        <div className={styles.introduction}>
                            {i18n.get("lottery.text_introduction")}
                        </div>
                        <div className={styles.mention}>
                            {i18n.get("lottery.text_mention")}
                        </div>
                        <div className={styles.rows}>
                            <div className={styles.row}>
                                <div className={styles.label}>
                                    {i18n.get("lottery.placeholder_name")}
                                </div>
                                <div
                                    className={cls(styles.value, styles.ellipsis)}
                                >
                                    {c.name}
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div
                                    className={cls(styles.label, styles.phone)}
                                >
                                    {i18n.get("lottery.placeholder_phone")}
                                </div>
                                <div
                                    className={cls(styles.value, styles.ellipsis)}
                                >
                                    {c.phone}
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.label}>
                                    {i18n.get("lottery.placeholder_email")}
                                </div>
                                <div
                                    className={cls(styles.value, styles.ellipsis)}
                                >
                                    {(c as any).email}
                                </div>
                            </div>
                            <div className={cls(styles.row, styles.addressRow)}>
                                <div className={styles.label}>
                                    {i18n.get("lottery.placeholder_address")}
                                </div>
                                <div
                                    className={cls(
                                        styles.value,
                                        styles.address,
                                        addressSmaller && styles.smaller,
                                    )}
                                >
                                    <div>{combineAddress}</div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}
                {step === RealRewardStep.CONFIRM && isSecondConfirm ? (
                    <div className={styles.confirm}>
                        {i18n.get("lottery.text_confirm")}
                    </div>
                ) : null}
                {step === RealRewardStep.DONE && (
                    <>
                        <div className={styles.introduction}>
                            {i18n.get("lottery.text_introduction")}
                        </div>
                        <div className={styles.mention}>
                            {i18n.get("lottery.text_mention")}
                        </div>
                    </>
                )}
            </div>
            <button
                type="button"
                className={styles.ruleButton}
                onClick={() => setRuleOpen(true)}
            >
                <StrokeText
                    text={i18n.get("lottery.btn_rule")}
                    fontFamily="a한글세상B"
                    fontSize={27}
                    fillColor="#ffffff"
                    borderWidth={4}
                    borderColor="#564426"
                    offsetY={0}
                    padding={4}
                />
            </button>
            <RuleModal
                open={ruleOpen}
                onConfirm={() => setRuleOpen(false)}
                onClose={() => setRuleOpen(false)}
                loading={false}
                showOkBtn={false}
            />
        </ModalContent>
    )
}
